import { useEffect } from "react";

// Moved verbatim from CoachProfileCard.jsx (#1069) so PlayerProfile.jsx's
// kitType Club/Team fields grow the same way the coach Headline/Teams do
// (#824/#1014).
/**
 * #824 — Headline and Teams you've coached are one-line values, but the
 * seeded values (and plenty of real ones) are wider than the 390px box.
 * kitType-only: grows a `rows={1}` textarea to fit its content instead of
 * scrolling text inside a fixed-height box. Runs on mount and on every
 * `value` change; `el.style.height = "auto"` first so shrinking a value
 * shrinks the box back down, not just grows it.
 *
 * `scrollHeight` never counts the element's own border (`Textarea`'s own
 * `border border-input`, 1px each side) — but under Tailwind preflight's
 * global `box-sizing: border-box`, the CSS `height` this sets DOES include
 * it. Journey 56i's own auto-grow assertion caught this: without adding
 * the border back, a long "Teams you've coached" value rendered 2px
 * short of its own content (`clientHeight` 2px under `scrollHeight`),
 * clipping the second line by that same 2px.
 *
 * #1014 review, F1/F2 — the same `value` can need a DIFFERENT height
 * later, with nothing that fires the effect above ever changing:
 *   - F1: a late-arriving web font (DM Sans) swaps glyph widths in after
 *     the first measure, reflowing how many lines the same text takes —
 *     caught by a reviewer probe (`fonts.gstatic.com` routed with a
 *     delay) that found the box stuck at its fallback-font height with
 *     the third line clipped under `overflow: hidden`.
 *   - F2: a viewport resize or rotation changes the element's own width,
 *     which also changes how many lines the same text wraps to.
 * `measure` (below) is re-run from a `ResizeObserver` on the element's own
 * box (covers F2, and any other width-changing cause — a sidebar toggle,
 * a text-size change) and from `document.fonts.ready` / the `loadingdone`
 * event (covers F1). Both are guarded: jsdom (this file's own unit tests)
 * has neither `ResizeObserver` nor `document.fonts`.
 */
export function useAutoGrow(ref, value) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const { borderTopWidth, borderBottomWidth } = window.getComputedStyle(el);
    const borders = parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [ref, value]);

  useEffect(() => {
    const el = ref.current;
    // `[ref, value]`, not just `[ref]` — `ref` is a `useRef` object and
    // never changes identity, so an effect keyed on it alone runs exactly
    // ONCE, on the render where `ref.current` is READ, not written: on
    // this form's first render `form` is still `null` (the profile GET
    // hasn't resolved yet), the Textarea doesn't exist, and `ref.current`
    // is `null` — an effect keyed only on `[ref]` would register its
    // ResizeObserver/font listeners against that `null`, bail out via the
    // guard below, and then NEVER run again once the real element mounts,
    // since React has no way to know `ref.current` changed underneath a
    // ref that never re-renders on its own (reproduced directly: a real
    // run showed the mount log this effect's own console.log briefly
    // carried never firing at all, while the sibling effect below, keyed
    // on `[ref, value]`, fired on every value change as expected).
    if (!el) return undefined;

    const measure = () => {
      el.style.height = "auto";
      const { borderTopWidth, borderBottomWidth } = window.getComputedStyle(el);
      const borders = parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);
      el.style.height = `${el.scrollHeight + borders}px`;
    };

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(el);
    }

    let cancelled = false;
    // `document.fonts.ready` resolving does not guarantee the browser has
    // already RE-LAID-OUT this element with the newly-swapped-in font —
    // measuring in the same microtask can still read the OLD (fallback-
    // font) `scrollHeight` (reproduced directly: a real run read the
    // fallback font's height here even after `fonts.ready` had resolved
    // and `document.fonts.check(...)` confirmed the real font was
    // active). A `requestAnimationFrame` after the signal waits for that
    // repaint to actually happen first.
    const measureNextFrame = () => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => {
          if (!cancelled) measure();
        });
      } else {
        measure();
      }
    };
    const fonts = typeof document !== "undefined" ? document.fonts : null;
    fonts?.ready?.then(() => {
      if (!cancelled) measureNextFrame();
    });
    fonts?.addEventListener?.("loadingdone", measureNextFrame);

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      fonts?.removeEventListener?.("loadingdone", measureNextFrame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, value]);
}
